import _ from "lodash";
import { sureClone, objectDiff } from "../lib/DataUtils";

/**
 * Helper for creating Pinia stores with common state setters similar to Vuex ModuleBase
 * @param {Object} options - Store options
 * @param {Object} options.state - Initial state
 * @param {Object} options.getters - Computed properties
 * @param {Object} options.actions - Async methods
 * @param {Object} options.persist - Persistence options
 */
export function createStore({
  state = {},
  getters = {},
  actions = {},
  persist = false,
}) {
  return {
    state:
      typeof state === "function"
        ? () => {
            const stateObj = state();
            return {
              ...stateObj,
              _initial: sureClone(stateObj),
            };
          }
        : () => ({
            ...state,
            _initial: sureClone(state),
          }),

    getters: {
      ...getters,
    },

    actions: {
      ...actions,

      getEditorEntity(collectionKey) {
        const collection = _.get(this, collectionKey);
        const editor = this.editor;

        if (!collection || !editor) {
          return null;
        }

        const candidateIds = [];
        const primaryId = _.get(editor, "id");
        if (primaryId !== null && primaryId !== undefined) {
          candidateIds.push(primaryId);
        }

        Object.entries(editor).forEach(([key, value]) => {
          if (
            key.endsWith("Id") &&
            value !== null &&
            value !== undefined &&
            !candidateIds.includes(value)
          ) {
            candidateIds.push(value);
          }
        });

        const matchedId = candidateIds.find((candidateId) =>
          _.has(collection, candidateId),
        );

        if (matchedId === undefined) {
          return null;
        }

        return {
          id: matchedId,
          original: _.get(collection, matchedId),
        };
      },

      // Helper for computing editor changes similar to Vuex pattern
      getEditorChanges(collectionKey) {
        const editorEntity = this.getEditorEntity(collectionKey);
        if (!editorEntity) {
          return null;
        }
        return objectDiff(editorEntity.original, this.editor);
      },

      // Similar to Vuex ModuleBase mutations
      set(key, value) {
        if (key.match(/\.|\[/)) {
          const parentPath = key.split(".").slice(0, -1).join(".");
          const childKey = key.split(".").pop();
          const parent = _.get(this, parentPath);
          if (parent) {
            parent[childKey] = value;
          }
        } else {
          this[key] = value;
        }
      },

      unset(key) {
        if (key.match(/\.|\[/)) {
          const parentPath = key.split(".").slice(0, -1).join(".");
          const childKey = key.split(".").pop();
          const parent = _.get(this, parentPath);
          if (parent) {
            delete parent[childKey];
          }
        } else {
          delete this[key];
        }
      },

      empty(key) {
        if (key.match(/\.|\[/)) {
          const parentPath = key.split(".").slice(0, -1).join(".");
          const childKey = key.split(".").pop();
          const parent = _.get(this, parentPath);
          if (parent) {
            parent[childKey] = null;
          }
        } else {
          this[key] = null;
        }
      },

      mapSet(path, id, payload) {
        const parent = _.get(this, path);
        if (parent) {
          parent[id] = payload;
        }
      },

      mapUnset(path, id) {
        const parent = _.get(this, path);
        if (parent) {
          delete parent[id];
        }
      },

      resetDefaultState() {
        const cleanState = sureClone(this._initial);
        Object.keys(cleanState).forEach((key) => {
          this[key] = cleanState[key];
        });
      },
    },

    ...(persist && {
      persist: typeof persist === "object" ? persist : true,
    }),
  };
}
